/**
 * Specification Location Quantities Hook
 * 
 * Manages the quantity allocation of specifications across multiple delivery
 * locations for a job. This enables complex distribution scenarios where
 * products/services need to be split across different sites.
 * 
 * **Quantity Matrix Structure:**
 * - Rows: Job specifications (products/services)
 * - Columns: Delivery locations (sites/addresses)
 * - Cells: Quantities or percentages for each spec-location pair
 * 
 * **Quantity Entry Fields:**
 * - `quantity`: Absolute quantity value
 * - `percentage`: Percentage of total quantity
 * - `notes`: Additional delivery instructions
 * 
 * **Data Organization:**
 * - **Array Format**: Linear list of quantity entries
 * - **Matrix Format**: Nested object for efficient lookup
 *   - `matrix[specId][locationId]` → quantity data
 * 
 * **Key Features:**
 * - Fetch quantities organized by specification and location
 * - Save individual quantity entries
 * - Batch save multiple quantities
 * - Calculate totals by specification
 * - Calculate totals by location
 * - Retrieve specific quantity values
 * 
 * **Use Cases:**
 * - Multi-site distribution planning
 * - Job fulfillment scheduling
 * - Delivery coordination
 * - Inventory allocation
 * - Vendor bid quantity breakdown
 * 
 * **Calculation Helpers:**
 * - `getQuantity`: Get quantity for spec-location pair
 * - `getSpecificationTotal`: Sum all locations for a spec
 * - `getLocationTotal`: Sum all specs for a location
 * 
 * @param {string} jobId - The job ID to fetch quantities for
 * 
 * @returns {Object} Specification location quantities
 * @returns {QuantityEntry[]} quantities - Array of quantity entries
 * @returns {QuantityMatrix} matrix - Nested object for quick lookup
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} saveQuantity - Save single quantity entry
 * @returns {Function} batchSaveQuantities - Save multiple quantities
 * @returns {Function} getQuantity - Get quantity for spec-location
 * @returns {Function} getSpecificationTotal - Sum quantities for spec
 * @returns {Function} getLocationTotal - Sum quantities for location
 * 
 * @example
 * // Fetch and display quantities
 * const {
 *   matrix,
 *   getSpecificationTotal,
 *   getLocationTotal
 * } = useSpecificationLocationQuantities(jobId);
 * 
 * specifications.map(spec => (
 *   <Row key={spec.id}>
 *     <Cell>{spec.name}</Cell>
 *     {locations.map(loc => (
 *       <Cell key={loc.id}>
 *         {matrix[spec.id]?.[loc.id]?.quantity || 0}
 *       </Cell>
 *     ))}
 *     <Cell>{getSpecificationTotal(spec.id)}</Cell>
 *   </Row>
 * ));
 * 
 * @example
 * // Save quantity allocation
 * const { saveQuantity } = useSpecificationLocationQuantities(jobId);
 * 
 * await saveQuantity(
 *   specificationId,
 *   locationId,
 *   100,
 *   'Deliver to dock A'
 * );
 * 
 * @example
 * // Batch update all quantities
 * const { batchSaveQuantities } = useSpecificationLocationQuantities(jobId);
 * 
 * const allQuantities = specifications.flatMap(spec =>
 *   locations.map(loc => ({
 *     job_id: jobId,
 *     specification_id: spec.id,
 *     delivery_location_id: loc.id,
 *     quantity: calculateQuantity(spec, loc)
 *   }))
 * );
 * 
 * await batchSaveQuantities(allQuantities);
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

interface QuantityEntry {
  id?: string;
  job_id: string;
  specification_id: string;
  delivery_location_id: string;
  quantity?: number;
  percentage?: number;
  notes?: string;
}

interface QuantityMatrix {
  [specId: string]: {
    [locationId: string]: {
      quantity?: number;
      percentage?: number;
      notes?: string;
      id?: string;
    };
  };
}

export const useSpecificationLocationQuantities = (jobId: string) => {
  const [quantities, setQuantities] = useState<QuantityEntry[]>([]);
  const [matrix, setMatrix] = useState<QuantityMatrix>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchQuantities = async () => {
      if (!jobId) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('job_specification_location_quantities')
          .select('*')
          .eq('job_id', jobId);

        if (error) throw error;

        setQuantities(data || []);
        
        const newMatrix: QuantityMatrix = {};
        (data || []).forEach(qty => {
          if (!newMatrix[qty.specification_id]) {
            newMatrix[qty.specification_id] = {};
          }
          newMatrix[qty.specification_id][qty.delivery_location_id] = {
            quantity: qty.quantity,
            percentage: qty.percentage,
            notes: qty.notes,
            id: qty.id,
          };
        });
        setMatrix(newMatrix);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching quantities:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch quantities');
      } finally {
        setLoading(false);
      }
    };

    fetchQuantities();
  }, [jobId]);

  const saveQuantity = async (
    specificationId: string,
    deliveryLocationId: string,
    quantity: number,
    notes?: string
  ) => {
    if (!user) throw new Error('User not authenticated');

    const existingId = matrix[specificationId]?.[deliveryLocationId]?.id;

    try {
      if (existingId) {
        const { error } = await supabase
          .from('job_specification_location_quantities')
          .update({
            quantity,
            notes,
            last_updated_by_user_id: user.id,
          })
          .eq('id', existingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('job_specification_location_quantities')
          .insert({
            job_id: jobId,
            specification_id: specificationId,
            delivery_location_id: deliveryLocationId,
            quantity,
            notes,
            created_by_user_id: user.id,
            last_updated_by_user_id: user.id,
          });

        if (error) throw error;
      }

      setMatrix(prev => ({
        ...prev,
        [specificationId]: {
          ...prev[specificationId],
          [deliveryLocationId]: { quantity, notes, id: existingId },
        },
      }));

      return { success: true };
    } catch (err) {
      console.error('Error saving quantity:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Failed to save' };
    }
  };

  const batchSaveQuantities = async (quantitiesToSave: QuantityEntry[]) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error: deleteError } = await supabase
        .from('job_specification_location_quantities')
        .delete()
        .eq('job_id', jobId);

      if (deleteError) throw deleteError;

      if (quantitiesToSave.length > 0) {
        const inserts = quantitiesToSave.map(qty => ({
          job_id: jobId,
          specification_id: qty.specification_id,
          delivery_location_id: qty.delivery_location_id,
          quantity: qty.quantity ?? 0, // Default to 0 to satisfy NOT NULL constraint
          percentage: qty.percentage || null,
          notes: qty.notes,
          created_by_user_id: user.id,
          last_updated_by_user_id: user.id,
        }));

        const { error: insertError } = await supabase
          .from('job_specification_location_quantities')
          .insert(inserts);

        if (insertError) throw insertError;
      }

      return { success: true };
    } catch (err) {
      console.error('Error batch saving quantities:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Failed to save' };
    }
  };

  const getQuantity = (specificationId: string, deliveryLocationId: string) => {
    return matrix[specificationId]?.[deliveryLocationId]?.quantity || 0;
  };

  const getSpecificationTotal = (specificationId: string) => {
    if (!matrix[specificationId]) return 0;
    return Object.values(matrix[specificationId]).reduce((sum, item) => sum + item.quantity, 0);
  };

  const getLocationTotal = (deliveryLocationId: string) => {
    let total = 0;
    Object.values(matrix).forEach(specMatrix => {
      if (specMatrix[deliveryLocationId]) {
        total += specMatrix[deliveryLocationId].quantity;
      }
    });
    return total;
  };

  return {
    quantities,
    matrix,
    loading,
    error,
    saveQuantity,
    batchSaveQuantities,
    getQuantity,
    getSpecificationTotal,
    getLocationTotal,
  };
};
